import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';

const apiBase = process.env.NODE_ENV === 'production' 
  ? 'https://proofofprice.com' 
  : 'http://localhost:3001';

// Styled components
const QueueContainer = styled.div`
  background: ${props => props.theme.surface};
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
`;

const QueueTitle = styled.h3`
  color: ${props => props.theme.text};
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Stats = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const StatCard = styled.div`
  background: ${props => props.theme.background};
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  padding: 15px;
  min-width: 120px;
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: ${props => props.theme.primary};
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: ${props => props.theme.subText};
  margin-top: 5px;
`;

const QueueTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${props => props.theme.background};
  border-radius: 4px;
  overflow: hidden;
`;

const TableHeader = styled.th`
  background: ${props => props.theme.surfaceDark || props.theme.surface};
  color: ${props => props.theme.text};
  padding: 12px;
  text-align: left;
  font-weight: bold;
  border-bottom: 1px solid ${props => props.theme.border};
`;

const TableCell = styled.td`
  padding: 12px;
  border-bottom: 1px solid ${props => props.theme.border};
  color: ${props => props.theme.text};
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  background: ${props => {
    switch (props.status) {
      case 'pending': return '#ff980020';
      case 'processing': return '#2196F320';
      case 'completed': return '#4CAF5020';
      case 'failed': return '#f4433620';
      default: return '#80808020';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'pending': return '#ff9800';
      case 'processing': return '#2196F3';
      case 'completed': return '#4CAF50';
      case 'failed': return '#f44336';
      default: return '#808080';
    }
  }};
  border: 1px solid ${props => {
    switch (props.status) {
      case 'pending': return '#ff9800';
      case 'processing': return '#2196F3';
      case 'completed': return '#4CAF50';
      case 'failed': return '#f44336';
      default: return '#808080';
    }
  }};
`;

const ActionButton = styled.button`
  background: ${props => props.theme.primary};
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  margin-right: 5px;
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

const RefreshButton = styled.button`
  background: #2196F3;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  
  &:hover {
    background: #1976D2;
  }
`;

const ErrorMessage = styled.div`
  color: #f44336;
  background: #f4433620;
  border: 1px solid #f44336;
  padding: 10px;
  border-radius: 4px;
  margin: 10px 0;
`;

interface QueueItem {
  id: number;
  tweet_url: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  created_at: string;
  processed_at?: string;
  expert_name?: string;
  error_message?: string;
  extracted_data?: any;
}

const TweetQueueManager: React.FC = () => {
  const [queueItems, setQueueItems] = useState<QueueItem[]>([]);
  const [stats, setStats] = useState({
    pending: 0,
    processing: 0,
    completed: 0,
    failed: 0,
    total: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadQueueData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch from the actual tweet queue API
      const response = await axios.get(`${apiBase}/api/tweet-queue`);
      
      if (response.data?.success && Array.isArray(response.data.queue)) {
        // Use the real queue data
        const realQueueItems: QueueItem[] = response.data.queue.map((item: any) => ({
          id: item.id,
          tweet_url: item.tweet_url,
          status: item.status,
          created_at: item.created_at,
          expert_name: item.expert_name || 'Unknown', // May not be available until processed
          extracted_data: item.result_data ? JSON.parse(item.result_data) : null
        }));

        setQueueItems(realQueueItems);

        // Calculate stats
        const newStats = realQueueItems.reduce((acc, item) => {
          acc[item.status] = (acc[item.status] || 0) + 1;
          acc.total++;
          return acc;
        }, { pending: 0, processing: 0, completed: 0, failed: 0, retrying: 0, total: 0 });

        setStats(newStats);
      }
    } catch (err: any) {
      console.error('Error loading queue data:', err);
      setError(err.response?.data?.error || 'Failed to load queue data');
      
      // Show demo data if API fails
      const demoItems: QueueItem[] = [
        {
          id: 1,
          tweet_url: 'https://x.com/saylor/status/1234567890',
          status: 'completed',
          created_at: new Date(Date.now() - 86400000).toISOString(),
          processed_at: new Date(Date.now() - 3600000).toISOString(),
          expert_name: 'Michael Saylor',
          extracted_data: { price_prediction: '$500,000', prediction_type: 'target' }
        },
        {
          id: 2,
          tweet_url: 'https://x.com/100trillionusd/status/9876543210',
          status: 'pending',
          created_at: new Date(Date.now() - 3600000).toISOString(),
          expert_name: 'Plan B'
        },
        {
          id: 3,
          tweet_url: 'https://x.com/cathiedwood/status/5555555555',
          status: 'failed',
          created_at: new Date(Date.now() - 7200000).toISOString(),
          expert_name: 'Cathie Wood',
          error_message: 'Tweet not accessible or deleted'
        }
      ];
      
      setQueueItems(demoItems);
      setStats({ pending: 1, processing: 0, completed: 1, failed: 1, total: 3 });
    } finally {
      setLoading(false);
    }
  };

  const processQueueItem = async (id: number) => {
    try {
      // Update status to processing
      setQueueItems(prev => prev.map(item => 
        item.id === id ? { ...item, status: 'processing' } : item
      ));

      // Simulate processing delay
      await new Promise(resolve => setTimeout(resolve, 2000));

      // In a real implementation, this would call your tweet processing API
      const response = await axios.post(`${apiBase}/api/process-tweet-queue-item`, { id });

      if (response.data.success) {
        setQueueItems(prev => prev.map(item => 
          item.id === id ? { 
            ...item, 
            status: 'completed', 
            processed_at: new Date().toISOString(),
            extracted_data: response.data.extracted_data 
          } : item
        ));
      } else {
        throw new Error(response.data.error || 'Processing failed');
      }
    } catch (err: any) {
      setQueueItems(prev => prev.map(item => 
        item.id === id ? { 
          ...item, 
          status: 'failed',
          error_message: err.message || 'Processing failed'
        } : item
      ));
    }

    // Recalculate stats
    loadQueueData();
  };

  const retryQueueItem = async (id: number) => {
    processQueueItem(id);
  };

  const deleteQueueItem = async (id: number) => {
    try {
      // In a real implementation, call delete API
      setQueueItems(prev => prev.filter(item => item.id !== id));
      // Recalculate stats
      loadQueueData();
    } catch (err) {
      console.error('Error deleting queue item:', err);
    }
  };

  useEffect(() => {
    loadQueueData();
  }, []);

  if (loading) {
    return (
      <QueueContainer>
        <QueueTitle>📋 Tweet Processing Queue</QueueTitle>
        <p>Loading queue data...</p>
      </QueueContainer>
    );
  }

  return (
    <QueueContainer>
      <QueueTitle>
        📋 Tweet Processing Queue
        <RefreshButton onClick={loadQueueData}>
          🔄 Refresh
        </RefreshButton>
      </QueueTitle>

      {error && (
        <ErrorMessage>
          ⚠️ {error} (Showing demo data below)
        </ErrorMessage>
      )}

      <Stats>
        <StatCard>
          <StatNumber>{stats.total}</StatNumber>
          <StatLabel>Total Items</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.pending}</StatNumber>
          <StatLabel>Pending</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.processing}</StatNumber>
          <StatLabel>Processing</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.completed}</StatNumber>
          <StatLabel>Completed</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.failed}</StatNumber>
          <StatLabel>Failed</StatLabel>
        </StatCard>
      </Stats>

      {queueItems.length === 0 ? (
        <p>No items in queue. Use the Tweet Extraction form above to add tweets.</p>
      ) : (
        <QueueTable>
          <thead>
            <tr>
              <TableHeader>ID</TableHeader>
              <TableHeader>Tweet URL</TableHeader>
              <TableHeader>Expert</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Created</TableHeader>
              <TableHeader>Actions</TableHeader>
            </tr>
          </thead>
          <tbody>
            {queueItems.map(item => (
              <tr key={item.id}>
                <TableCell>{item.id}</TableCell>
                <TableCell>
                  <a 
                    href={item.tweet_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ color: 'inherit', textDecoration: 'underline' }}
                  >
                    {item.tweet_url.length > 50 
                      ? `${item.tweet_url.substring(0, 50)}...` 
                      : item.tweet_url
                    }
                  </a>
                </TableCell>
                <TableCell>{item.expert_name || 'Unknown'}</TableCell>
                <TableCell>
                  <StatusBadge status={item.status}>
                    {item.status.toUpperCase()}
                  </StatusBadge>
                  {item.error_message && (
                    <div style={{ fontSize: '11px', color: '#f44336', marginTop: '2px' }}>
                      {item.error_message}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  {new Date(item.created_at).toLocaleString()}
                </TableCell>
                <TableCell>
                  {item.status === 'pending' && (
                    <ActionButton onClick={() => processQueueItem(item.id)}>
                      ▶️ Process
                    </ActionButton>
                  )}
                  {item.status === 'failed' && (
                    <ActionButton onClick={() => retryQueueItem(item.id)}>
                      🔄 Retry
                    </ActionButton>
                  )}
                  {item.status === 'completed' && item.extracted_data && (
                    <ActionButton 
                      style={{ background: '#4CAF50' }}
                    >
                      👁️ View
                    </ActionButton>
                  )}
                  <ActionButton 
                    onClick={() => deleteQueueItem(item.id)}
                    style={{ background: '#f44336' }}
                  >
                    🗑️ Delete
                  </ActionButton>
                </TableCell>
              </tr>
            ))}
          </tbody>
        </QueueTable>
      )}
    </QueueContainer>
  );
};

export default TweetQueueManager;